import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Gift, Copy, Check, Users, Award } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Referrals() {
  const [refs, setRefs] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referrals").then((r) => setRefs(r.data)).catch(() => {});
  }, []);

  if (!refs) return null;
  const link = `${window.location.origin}/login?ref=${refs.referral_code}`;
  const copy = () => {
    navigator.clipboard?.writeText(link);
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="max-w-3xl" data-testid="referrals-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Rewards</p>
        <h1 className="font-display text-3xl font-extrabold text-slate-900 dark:text-slate-100">Refer &amp; Earn</h1>
        <p className="text-muted-foreground text-sm mt-1">{refs.reward}</p>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
        <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-sm">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
          <div className="relative flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-brand-soft flex items-center justify-center shrink-0">
              <Gift className="w-6 h-6 text-brand" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 dark:text-slate-100">Share your invite link</div>
              <p className="text-sm text-muted-foreground mt-0.5">
                Send this to another vendor or contractor. When they subscribe, you both win.
              </p>
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <code data-testid="referral-link" className="flex-1 text-xs bg-secondary rounded-lg px-3 py-2.5 font-mono truncate">{link}</code>
                <Button data-testid="copy-referral-button" variant="outline" onClick={copy} className="gap-2 font-semibold">
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Copied" : "Copy link"}
                </Button>
              </div>
              <div className="mt-4 text-xs text-muted-foreground">
                Your code <span data-testid="referral-code" className="font-mono font-bold text-foreground">{refs.referral_code}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
              <Users className="w-4 h-4" /> Joined
            </div>
            <div data-testid="referral-total" className="text-3xl font-bold font-display mt-2 text-slate-900 dark:text-slate-100">{refs.summary?.total ?? 0}</div>
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider">
              <Award className="w-4 h-4" /> Rewarded
            </div>
            <div data-testid="referral-rewarded" className="text-3xl font-bold font-display mt-2 text-slate-900 dark:text-slate-100">{refs.summary?.rewarded ?? 0}</div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="font-semibold text-slate-900 dark:text-slate-100 mb-3">Who you've referred</div>
          {refs.referrals?.length > 0 ? (
            <div className="divide-y divide-border">
              {refs.referrals.map((x, i) => (
                <div key={i} data-testid={`referral-row-${i}`} className="py-3 flex items-center justify-between text-sm">
                  <span className="font-medium">{x.company_name || "New sign-up"}</span>
                  <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${x.status === "rewarded" ? "bg-brand-soft text-brand" : "bg-secondary text-muted-foreground"}`}>
                    {x.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No referrals yet. Share your link to get started.</p>
          )}
        </div>
      </motion.div>
    </div>
  );
}
